import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { fetchCalendar } from '@/api/calendar'
import { listRecurring } from '@/api/recurring'
import { addDays, comingUp, dayTitle, iso, occurrences } from '@/components/calendar/calendarModel'
import { ComingUp } from '@/components/calendar/ComingUp'
import { DayPanel } from '@/components/calendar/DayPanel'
import { MonthGrid } from '@/components/calendar/MonthGrid'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/sheet'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'

function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month: string) {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
}

export default function CalendarPage() {
  const wide = useMediaQuery(DESKTOP_QUERY)
  const today = iso(new Date())
  const thisMonth = today.slice(0, 7)
  const [month, setMonth] = useState(thisMonth)
  // Desktop always shows a day (today, or the 1st of another month); phone shows one only when tapped.
  const [picked, setPicked] = useState<string | null>(null)
  const selected = picked ?? (wide ? (month === thisMonth ? today : `${month}-01`) : null)

  const calendar = useQuery({ queryKey: ['calendar', month], queryFn: () => fetchCalendar(month) })
  const active = useQuery({
    queryKey: ['recurring', 'active'],
    queryFn: () => listRecurring('active'),
  })

  const reminders = useMemo(() => active.data?.items ?? [], [active.data])
  const monthEnd = shiftMonth(month, 1) + '-01'
  const dueByDate = useMemo(
    () => occurrences(reminders, `${month}-01`, addDays(monthEnd, -1)),
    [reminders, month, monthEnd]
  )
  const groups = useMemo(() => comingUp(reminders, today), [reminders, today])
  const days = calendar.data?.days ?? []
  const max = Math.max(0, ...days.map((d) => d.expenseTotal))

  // The phone sheet can step past the month's edge; the grid follows so the day's summary is right.
  function pick(date: string | null) {
    setPicked(date)
    if (date && date.slice(0, 7) !== month) setMonth(date.slice(0, 7))
  }

  function changeMonth(delta: number) {
    setMonth((m) => shiftMonth(m, delta))
    setPicked(null)
  }

  const panel = (date: string, showTitle = true) => {
    const spent = days.find((d) => d.date === date)?.expenseTotal ?? 0
    return (
      <DayPanel
        date={date}
        today={today}
        spent={spent}
        biggest={spent > 0 && spent === max}
        due={date > today ? (occurrences(reminders, date, date).get(date) ?? []) : []}
        showTitle={showTitle}
      />
    )
  }

  return (
    <div className="px-4 py-5 md:p-10">
      <header className="mb-4 flex items-center justify-between gap-3 md:mb-5">
        <h1 className="font-serif text-[22px] font-semibold text-[var(--fl-heading)] md:text-[30px]">
          Calendar
        </h1>
        <div className="flex items-center gap-2">
          {month !== thisMonth && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMonth(thisMonth)
                setPicked(null)
              }}
            >
              Today
            </Button>
          )}
          <div className="flex items-center rounded-[10px] border border-[var(--fl-border)] bg-[var(--fl-surface)]">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => changeMonth(-1)}
              tooltip="Previous month"
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium text-[var(--fl-heading)]">
              {monthLabel(month)}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => changeMonth(1)}
              tooltip="Next month"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </header>

      <div className="grid items-start gap-5 md:grid-cols-[1fr_360px]">
        <div className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-3 shadow-[var(--card-shadow-sm)] md:p-4">
          {calendar.isError ? (
            <div className="py-16 text-center">
              <p className="mb-3 text-sm text-[var(--fl-error)]">
                Couldn&apos;t load the calendar.
              </p>
              <Button variant="outline" onClick={() => calendar.refetch()}>
                Try again
              </Button>
            </div>
          ) : calendar.isLoading ? (
            <div className="grid grid-cols-7 gap-1.5" aria-busy="true">
              {Array.from({ length: 35 }, (_, i) => (
                <div
                  key={i}
                  className="min-h-[42px] animate-pulse rounded-[10px] bg-[var(--fl-surface-subtle)] md:min-h-[78px]"
                />
              ))}
            </div>
          ) : (
            <MonthGrid
              month={month}
              days={days}
              dueByDate={dueByDate}
              selected={selected}
              today={today}
              compact={!wide}
              onSelect={setPicked}
            />
          )}
        </div>

        <div className="flex flex-col gap-5">
          {wide && selected && (
            <div className="rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-4 shadow-[var(--card-shadow-sm)]">
              {panel(selected)}
            </div>
          )}
          <ComingUp
            groups={groups}
            today={today}
            failed={active.isError}
            onRetry={() => active.refetch()}
          />
        </div>
      </div>

      {!wide && picked && (
        <Sheet title={dayTitle(picked)} onClose={() => setPicked(null)}>
          <div className="mb-2 flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              tooltip="Previous day"
              onClick={() => pick(addDays(picked, -1))}
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              tooltip="Next day"
              onClick={() => pick(addDays(picked, 1))}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
          {panel(picked, false)}
        </Sheet>
      )}
    </div>
  )
}
